import * as React from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'

import {
  aggregateFieldOptions,
  selectAllCustomFieldOptions,
} from '@user/store/customFields'
import type {
  EditableCustomFieldProps,
  ICustomFieldValueFormModel,
} from '../typings'
import { TextField } from './TextField.styled'
import { useGetError } from '../useGetError'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'

type CustomFieldAutocompleteProps = EditableCustomFieldProps & {
  type: 'single' | 'multi'
  selectedOptions: null | number | number[]
  whiteBackground?: boolean
  avoidValidateOnBlur?: boolean
}

export function CustomFieldAutocomplete(props: CustomFieldAutocompleteProps) {
  const {
    type,
    margin,
    selectedOptions,
    field: { id: fieldId, name },
    fieldSetting: { required } = {},
    textFieldProps,
    whiteBackground = false,
    avoidValidateOnBlur = false,
  } = props

  const multiple = type === 'multi'
  const fieldName = `customFields.${type}[${fieldId}]`

  const { hasError, errorText } = useGetError(props)

  const { handleBlur, setFieldValue } =
    useFormikContext<ICustomFieldValueFormModel>()

  const allOptions = useSelector(selectAllCustomFieldOptions)

  const { fieldOptions, options } = useMemo(() => {
    const fieldOptions = aggregateFieldOptions(fieldId, allOptions)

    return {
      fieldOptions,
      options: fieldOptions?.filter((x) => x.active).map((x) => x.id),
    }
  }, [allOptions, fieldId])

  const getOptionLabel = useCallback(
    (id: number) => fieldOptions.find((x) => x.id === id)?.name ?? '',
    [fieldOptions],
  )

  const getOptionDisabled = useCallback(
    (id: number) => {
      const active = fieldOptions.find((x) => x.id === id)?.active ?? false
      return !active
    },
    [fieldOptions],
  )

  useEffect(() => {
    const activeOptions = fieldOptions.filter((x) => x.active)
    if (activeOptions?.length == 1 && required) {
      if (multiple) {
        setFieldValue(
          `${fieldName}.value`,
          activeOptions?.map((row) => row.id),
        )
      } else {
        setFieldValue(`${fieldName}.value`, activeOptions[0]?.id)
      }
    }
  }, [fieldOptions, fieldName, multiple, required, setFieldValue])

  const onChange = useCallback(
    (_e, value: number | number[]) => {
      setFieldValue(`${fieldName}.value`, value)
    },
    [fieldName, setFieldValue],
  )

  const helperText = hasError ? errorText : undefined

  return (
    <Autocomplete
      openOnFocus
      options={options}
      onChange={onChange}
      multiple={multiple}
      value={selectedOptions}
      getOptionLabel={getOptionLabel}
      ChipProps={{ color: 'primary' }}
      getOptionDisabled={getOptionDisabled}
      renderInput={(params) => {
        const startAdornment = params.InputProps?.startAdornment
        const isCustom =
          multiple &&
          Array.isArray(startAdornment) &&
          startAdornment?.length > 0
        const moreThanOneOption =
          Array.isArray(startAdornment) && startAdornment?.length > 1
        return (
          <TextField
            {...params}
            fullWidth
            label={name}
            margin={margin}
            name={fieldName}
            error={hasError}
            onBlur={!avoidValidateOnBlur ? handleBlur : undefined}
            required={required}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: isCustom ? (
                <>
                  {moreThanOneOption ? (
                    <Box px={1}>{startAdornment?.length + ' Selected'}</Box>
                  ) : (
                    <Box px={1}>
                      {startAdornment?.map((row) => {
                        return <Box key={row?.key}>{row?.props?.label}</Box>
                      })}
                    </Box>
                  )}
                </>
              ) : (
                startAdornment
              ),
              sx: {
                backgroundColor: whiteBackground
                  ? (theme: Theme) => theme.palette.common.white
                  : 'inherit',
              },
            }}
            {...textFieldProps}
            data-test-id={`custom-autocomplete-field-${fieldId}`}
          />
        )
      }}
    />
  )
}
